import { Message } from "primereact/message";
import { classNames } from "primereact/utils";
import {
  EQUIPE_ETAPA_APOIO,
  EQUIPE_ETAPA_SEM_PERIODO,
  EQUIPE_ETAPA_TITULO,
} from "../nova-obra.constants";
import { AdicionarPapel } from "@/shared/components/equipe-obra/adicionar-papel";
import { CalendarioAlocacaoTab } from "@/shared/components/equipe-obra/calendario-alocacao-tab";
import { ConflitosAlocacaoDialog } from "@/shared/components/equipe-obra/conflitos-alocacao-dialog";
import { EquipeIncompletaDialog } from "@/shared/components/equipe-obra/equipe-incompleta-dialog";
import { EquipeTabela } from "@/shared/components/equipe-obra/equipe-tabela";
import { CalendarioVazio, EquipeVazia } from "@/shared/components/equipe-obra/equipe-vazia";
import { ProfissionalIndisponivelDialog } from "@/shared/components/equipe-obra/profissional-indisponivel-dialog";
import {
  EQUIPE_CALENDARIO_EMPTY,
  EQUIPE_SECTION_TITLE,
  EQUIPE_TABS_LABEL,
} from "@/shared/components/equipe-obra/equipe-obra.constants";
import { PapeisObrigatorios } from "@/shared/components/papeis-obrigatorios";
import styles from "./etapa-equipe.module.scss";
import type { EtapaEquipeProps, EtapaEquipeViewModel } from "./types-etapa-equipe";

/**
 * Etapa 4 "Equipe" (HU 2530): as mesmas regras de "Definir Equipe de Obra" (HU 2050), inline no
 * wizard. As alocações ficam no cliente (R3) e são validadas contra o período definido na Etapa 2
 * (Impasse 21); a gravação é do POST da HU 2543.
 */
export function EtapaEquipeView(props: EtapaEquipeProps & EtapaEquipeViewModel) {
  const {
    linhas,
    periodo,
    today,
    opcoes,
    abas,
    abaAtivaId,
    calendarioAtivo,
    selecionarAba,
    requiredOpen,
    alternarPapeisObrigatorios,
    carregandoOpcoes,
    adicionarPapelDesabilitado,
    conflitos,
    indisponiveis,
    itensIncompletos,
    formError,
    obrigatorios,
    papeisOpcoes,
    adicionados,
    changeRow,
    addRow,
    confirmRow,
    cancelRow,
    startEdit,
    removeRow,
    obraVerificada,
    novas,
    calendarioVazio,
    conflitosDe,
    verificacoes,
  } = props;

  return (
    <div className={styles.body} data-testid="etapa-equipe">
      <header className={styles.header}>
        <h2 className={styles.title}>{EQUIPE_ETAPA_TITULO}</h2>
        <p className={styles.subtitle}>{EQUIPE_ETAPA_APOIO}</p>
      </header>

      <div className={styles.tabs} role="tablist" aria-label={EQUIPE_TABS_LABEL}>
        {abas.map((aba) => (
          <button
            key={aba.id}
            type="button"
            role="tab"
            id={aba.domId}
            className={`${styles.tab} ${aba.ativa ? styles.tabActive : ""}`}
            aria-selected={aba.ativa}
            aria-controls="equipe-obra-panel"
            onClick={() => selecionarAba(aba.id)}
            data-testid={aba.domId}
          >
            {aba.label}
          </button>
        ))}
      </div>

      {!periodo ? (
        <Message
          severity="warn"
          className={classNames("p-message-block", styles.aviso)}
          data-testid="equipe-sem-periodo"
          text={EQUIPE_ETAPA_SEM_PERIODO}
        />
      ) : null}

      <div
        className={styles.panel}
        role="tabpanel"
        id="equipe-obra-panel"
        aria-labelledby={abaAtivaId}
      >
        {calendarioAtivo ? (
          calendarioVazio ? (
            <CalendarioVazio mensagem={EQUIPE_CALENDARIO_EMPTY} />
          ) : (
            <CalendarioAlocacaoTab
              obra={obraVerificada}
              novas={novas}
              profissionais={opcoes.profissionais}
              today={today}
            />
          )
        ) : (
          <>
            <PapeisObrigatorios
              papeis={obrigatorios}
              adicionados={adicionados}
              periodo={periodo}
              hoje={today}
              carregando={carregandoOpcoes}
              aberto={requiredOpen}
              onAlternar={alternarPapeisObrigatorios}
              onAdicionar={addRow}
            />

            <section className={styles.team} data-testid="equipe-obra-team">
              <div className={styles.topbar}>
                <h3 className={styles.teamTitle}>{EQUIPE_SECTION_TITLE}</h3>
                <AdicionarPapel
                  papeis={opcoes.papeis}
                  periodo={periodo}
                  temLinhas={linhas.length > 0}
                  disabled={adicionarPapelDesabilitado}
                  onAdicionar={addRow}
                />
              </div>

              {formError ? (
                <Message
                  severity="error"
                  data-testid="equipe-obra-form-error"
                  className="p-message-block"
                  text={formError}
                />
              ) : null}

              {linhas.length === 0 ? (
                <EquipeVazia />
              ) : (
                <EquipeTabela
                  linhas={linhas}
                  papeis={papeisOpcoes}
                  profissionais={opcoes.profissionais}
                  profissionaisError={opcoes.profissionaisError}
                  conflitosDe={conflitosDe}
                  onChange={changeRow}
                  onConfirm={confirmRow}
                  onCancel={cancelRow}
                  onEdit={startEdit}
                  onRemove={removeRow}
                />
              )}
            </section>
          </>
        )}
      </div>

      {conflitos ? (
        <ConflitosAlocacaoDialog
          obra={obraVerificada}
          conflitos={conflitos.conflitos}
          maxJustificativa={verificacoes.maxJustificativa}
          onVoltar={verificacoes.cancelar}
          onConfirmar={verificacoes.confirmarConflitos}
        />
      ) : null}

      {indisponiveis ? (
        <ProfissionalIndisponivelDialog
          itens={indisponiveis.itens}
          maxJustificativa={verificacoes.maxJustificativa}
          onVoltar={verificacoes.cancelar}
          onConfirmar={verificacoes.confirmarIndisponivel}
        />
      ) : null}

      <EquipeIncompletaDialog
        itens={itensIncompletos}
        obraRotulo={obraVerificada.codigo}
        onHide={verificacoes.cancelar}
        onConfirm={verificacoes.confirmarIncompleta}
      />
    </div>
  );
}
